import React from 'react'
import styled, { css } from 'styled-components'

// Components
import JobTag from '../tag/JobTag'
import Text from '../Text'

// Styles
import { mqFrom, mqTo } from '../../styles/mediaQueries'
import { fastTransition } from '../../styles/effects'

interface IProps {
  className?: string
  children: React.ReactNode
}

interface ICardLayoutProps {
  renderFunction: (array: any[]) => React.ReactNode
  array: any[]
  className?: string
}

export const Row: React.FC<IProps> = ({ className, children }) => (
  <RowContainer className={className}>{children}</RowContainer>
)

export const CardLayout: React.FC<ICardLayoutProps> = ({
  className,
  renderFunction,
  array,
}) => (
  <CardsContainer className={className}>
    <Column>
      {renderFunction(array.slice(0, Math.ceil(array.length / 2)))}
    </Column>
    <Column> {renderFunction(array.slice(Math.ceil(array.length / 2)))}</Column>
  </CardsContainer>
)

const sharedRowStyles = css`
  display: flex;
  flex-direction: column;

  ${mqFrom.M`
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
  `}
`

const RowContainer = styled.div`
  ${sharedRowStyles};
`

export const WrapRow = styled.div`
  ${sharedRowStyles};

  ${mqFrom.M`
    flex-wrap: wrap;
  `}
`

// Row card (table) styled components to re use
export const RowActions = styled.div`
  display: flex;
  flex-direction: column;

  ${mqFrom.M`
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    width: 60%;
  `}
`

export const RowAction = styled.div`
  width: 100%;
`

export const RowDetails = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  position: relative;

  ${mqFrom.M`
    width: 40%;
    margin-bottom: 0;
  `}
`

export const MobileButton = styled.div`
  display: block;
  flex-shrink: 0;
  margin-left: auto;

  ${mqFrom.M`
    display: none;
  `}
`

export const DesktopButton = styled.div`
  display: none;

  ${mqFrom.M`
    display: block;
    width: auto;
    flex-shrink: 0;
    opacity: 0;
    transition: opacity ${fastTransition};
  `}
`

export const MobileText = styled(Text)`
  ${mqFrom.M`
    display: none;
  `}
`

export const TagText = styled(Text)`
  ${mqTo.M`
    margin-left: auto;
  `}
`

export const Tag = styled(JobTag)`
  transition: background-color ${fastTransition};
  height: 26px;
  margin-top: 8px;
  width: 100%;

  ${mqFrom.M`
    margin: 0;
    width: unset;
  `}

  ${mqTo.M`
    background-color: ${({ theme }) => theme.colors.primaryLight};
  `}
`

const activeStyles = () => css`
  background-color: ${({ theme }) => theme.colors.lightest};

  ${DesktopButton} {
    opacity: 1;
  }

  ${Tag} {
    background-color: ${({ theme }) => theme.colors.primaryLight};
  }
`

export const RowWrapper = styled.div<{ showBtn?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  transition: background-color 100ms ease-in-out;
  padding: 8px 12px;

  ${mqFrom.M`
    justify-content: space-between;
    flex-direction: row;
    align-items: center;
  `}

  ${mqTo.M`
    background-color: ${({ theme }) => theme.colors.lightest};
  `}

  &:hover {
    ${activeStyles()}
  }

  ${({ showBtn }) =>
    showBtn &&
    css`
      ${activeStyles()}
    `}
`

export const RowAligned = styled.div<{ margin?: string }>`
  display: flex;
  align-items: center;
  ${({ margin }) => margin && `margin: ${margin};`}
`

export const RowCentered = styled(RowAligned)`
  justify-content: center;
`

// Card layout styled components
const CardsContainer = styled(Row)`
  ${mqFrom.M`
    align-items: flex-start;
  `}
`

const Column = styled.div`
  ${mqFrom.M`
    width: calc(50% - 12px);
  `}
`
